// Signing in from the page. The accounts system is Better Auth on the Studio
// origin (docs/accounts.md); these are the calls a sign-in card makes to it.
// Each rejects with an Error whose message can be shown as it is.

import { t } from "../ui/lang.ts";

/** A refused sign-in call: `message` can be shown as it is, and `code` and `rule` say which refusal it was. */
export class SignInRefusal extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly rule?: string,
  ) {
    super(message);
  }
}

async function post<T>(path: string, body: unknown, headers: Record<string, string> = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json", ...headers },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error(t("signin.offline"));
  }
  const reply = (await response.json().catch(() => null)) as (T & { message?: string; code?: string; rule?: string }) | null;
  if (!response.ok) throw new SignInRefusal(describe(response.status, reply?.code, reply?.message), reply?.code, reply?.rule);
  return reply as T;
}

/** What to tell a visitor for a refused sign-in call. */
function describe(status: number, code: string | undefined, message: string | undefined): string {
  // The Studio's own limit on codes by email says which limit and how long (src/core/codes.ts).
  if (code === "CODE_LIMIT" && message) return message;
  if (status === 429) return t("signin.tooMany");
  if (code === "INVALID_OTP") return t("signin.wrongCode");
  if (code === "OTP_EXPIRED") return t("signin.codeExpired");
  if (code === "TOO_MANY_ATTEMPTS") return t("signin.tooManyCodes");
  if (code === "INVALID_EMAIL") return t("signin.badEmail");
  return message && message.length < 120 ? message : t("signin.failed");
}

/** Where the provider sends the visitor back to: this page, as it is. */
const here = () => location.pathname + location.search;

/** Leave for Google or GitHub; the provider returns the visitor here, signed in. */
export async function signInWith(provider: "google" | "github"): Promise<void> {
  const { url } = await post<{ url: string }>("/api/auth/sign-in/social", { provider, callbackURL: here(), errorCallbackURL: here() });
  location.assign(url);
}

/**
 * Leave for GitHub to link a GitHub account to the account signed in; GitHub
 * returns the visitor here. A link GitHub or Better Auth refuses returns here
 * with `?error=<code>`, such as `account_already_linked_to_different_user`.
 */
export async function linkWith(provider: "github"): Promise<void> {
  const { url } = await post<{ url: string }>("/api/auth/link-social", { provider, callbackURL: here(), errorCallbackURL: here() });
  location.assign(url);
}

/** Email a six-digit code to `email`, in the language `lang` names (the mail is written in it). */
export async function sendCode(email: string, lang?: string): Promise<void> {
  await post("/api/auth/email-otp/send-verification-otp", { email, type: "sign-in" }, lang ? { "x-pocket-lang": lang } : {});
}

/** Sign in with the code that was emailed. */
export async function signInWithCode(email: string, code: string): Promise<void> {
  await post("/api/auth/sign-in/email-otp", { email, otp: code });
}
